import { DEFAULT_SPORT, normalizeSportValue } from '../constants/sports'

// Reglas de edad de una categoría, en un solo lugar.
//
// Estaban repartidas en seis pantallas, cada una con su copia, y no coincidían: dos
// imprimían el rango al revés ("Nacidos 2017-2016") y una llamaba "nac. max" al año
// más temprano. El vocabulario, de una vez y para todas:
//
//   FLAG guarda un rango de años de nacimiento:
//     anio_inicio = el año MÁS TARDE permitido  -> el jugador más CHICO
//     anio_fin    = el año MÁS TEMPRANO         -> el jugador más GRANDE
//   Suena al revés, y por eso se equivocaban: el rango se escribe fin-inicio.
//
//   TACKLE guarda dos límites con su fecha de corte:
//     sup = el jugador más GRANDE permitido  -> la fecha de nacimiento más temprana
//     inf = el jugador más CHICO             -> la más tardía
//   Más el peso máximo.
//
// Ojo con lo que NO resuelve esto: las dos formas guardan una foto absoluta de un
// concepto relativo. "U10" son hoy los nacidos 2016-2017; el 1 de enero deberían ser
// 2017-2018, y la fila no cambia sola. Ni las edades ni los años se recorren.

// Una edad de categoría no pasa de aquí. Sin este tope se colaron años de nacimiento
// (2015, 2021) en columnas de edad: el código hace `currentYear - edad`, así que
// 2026-2016 = 10, y `new Date(10, 0, 1)` es 1910 -- JS mapea los años 0-99 a 1900+.
// La ventana válida se iba un siglo atrás y nadie quedaba dentro de su categoría.
export const EDAD_MAX_CATEGORIA = 60

const pareceAnio = (valor) => valor !== null && valor !== undefined && valor >= 1900

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)

export const validateTackleLimits = (payload = {}) => {
  const supAge = num(payload.tackle_edad_sup)
  const infAge = num(payload.tackle_edad_inf)

  if (pareceAnio(payload.tackle_edad_sup) || pareceAnio(payload.tackle_edad_inf)) {
    return 'Captura la EDAD en años (por ejemplo 12), no el año de nacimiento.'
  }
  if ((supAge !== null && supAge > EDAD_MAX_CATEGORIA) || (infAge !== null && infAge > EDAD_MAX_CATEGORIA)) {
    return `La edad no puede ser mayor a ${EDAD_MAX_CATEGORIA} años.`
  }
  if ((supAge !== null && supAge < 1) || (infAge !== null && infAge < 1)) {
    return 'La edad debe ser de al menos 1 año.'
  }

  const dias = [num(payload.tackle_limite_sup_dia), num(payload.tackle_limite_inf_dia)]
  if (dias.some((d) => d !== null && (d < 1 || d > 31))) {
    return 'El día de corte debe estar entre 1 y 31.'
  }
  const meses = [num(payload.tackle_limite_sup_mes), num(payload.tackle_limite_inf_mes)]
  if (meses.some((m) => m !== null && (m < 1 || m > 12))) {
    return 'El mes de corte debe estar entre 1 y 12.'
  }

  // sup es el jugador más grande: su edad no puede ser menor que la del más chico.
  if (supAge !== null && infAge !== null && supAge < infAge) {
    return 'El jugador más grande permitido no puede tener menos edad que el más chico.'
  }

  return null
}

const ddmm = (dia, mes) => {
  const d = num(dia)
  const m = num(mes)
  if (!d || !m) return null
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`
}

// Los años que abarca la categoría. `min` es el más temprano (el jugador más grande)
// y `max` el más tardío (el más chico). De aquí salen la etiqueta Y las edades, para
// que las dos mitades del texto no puedan contradecirse.
export const anniosCategoria = (categoria = {}, currentYear = new Date().getFullYear()) => {
  if (normalizeSportValue(categoria.deporte || DEFAULT_SPORT) === 'tackle') {
    const sup = num(categoria.tackle_edad_sup)
    const inf = num(categoria.tackle_edad_inf)
    return { min: sup !== null ? currentYear - sup : null, max: inf !== null ? currentYear - inf : null }
  }
  const min = num(categoria.anio_fin) ?? (num(categoria.edad_max) !== null ? currentYear - num(categoria.edad_max) : null)
  const max = num(categoria.anio_inicio) ?? (num(categoria.edad_min) !== null ? currentYear - num(categoria.edad_min) : null)
  return { min, max }
}

// " (9-10 años)" — el mismo rango en la otra unidad, derivado de los años para que
// nunca diga algo distinto que la primera mitad.
const sufijoEdades = (categoria, currentYear) => {
  const { min, max } = anniosCategoria(categoria, currentYear)
  // Nacer ANTES es ser MÁS GRANDE: el año mínimo da la edad máxima.
  const edadMax = min !== null ? currentYear - min : null
  const edadMin = max !== null ? currentYear - max : null
  if (edadMin !== null && edadMax !== null) {
    return edadMin === edadMax ? ` (${edadMin} años)` : ` (${edadMin}-${edadMax} años)`
  }
  if (edadMax !== null) return ` (${edadMax} años o menos)`
  if (edadMin !== null) return ` (${edadMin} años o más)`
  return ''
}

const rangoTackle = (categoria, currentYear) => {
  const pesoMax = num(categoria.tackle_peso_max_kg)
  const { min: anioMin, max: anioMax } = anniosCategoria(categoria, currentYear)

  const partes = []
  if (anioMin !== null && anioMax !== null) {
    partes.push(anioMin === anioMax ? `Nacidos en ${anioMin}` : `Nacidos ${anioMin}-${anioMax}`)
  } else if (anioMin !== null) {
    partes.push(`Nacidos desde ${anioMin}`)
  } else if (anioMax !== null) {
    partes.push(`Nacidos hasta ${anioMax}`)
  }

  const corteSup = ddmm(categoria.tackle_limite_sup_dia, categoria.tackle_limite_sup_mes)
  const corteInf = ddmm(categoria.tackle_limite_inf_dia, categoria.tackle_limite_inf_mes)
  if (corteSup || corteInf) {
    partes.push(`Corte ${[corteSup, corteInf].filter(Boolean).join(' y ')}`)
  }
  if (pesoMax !== null) partes.push(`Peso máx ${pesoMax} kg`)

  return partes.length > 0 ? partes.join(' · ') : 'Rango por definir'
}

// La etiqueta del rango, igual en todas las pantallas.
export const rangoCategoria = (categoria = {}, { sportFallback = DEFAULT_SPORT, currentYear, conEdades = false } = {}) => {
  const anio = currentYear || new Date().getFullYear()
  const cat = { ...categoria, deporte: categoria.deporte || sportFallback }
  const edades = conEdades ? sufijoEdades(cat, anio) : ''

  if (normalizeSportValue(cat.deporte) === 'tackle') {
    return rangoTackle(categoria, anio) + edades
  }

  const anioInicio = num(categoria.anio_inicio)
  const anioFin = num(categoria.anio_fin)
  if (anioInicio !== null || anioFin !== null) {
    if (anioInicio !== null && anioFin !== null) {
      return (anioInicio === anioFin ? `Nacidos en ${anioFin}` : `Nacidos ${anioFin}-${anioInicio}`) + edades
    }
    // anio_inicio solo = hay tope por abajo en edad (el más chico), o sea año máximo.
    return (anioInicio !== null ? `Nacidos hasta ${anioInicio}` : `Nacidos desde ${anioFin}`) + edades
  }

  const edadMin = num(categoria.edad_min)
  const edadMax = num(categoria.edad_max)
  if (edadMin !== null && edadMax !== null) {
    return categoria.sin_limite_superior ? `Desde ${edadMin} años` : `${edadMin}-${edadMax} años`
  }
  if (edadMin !== null) return `Desde ${edadMin} años`
  if (edadMax !== null) return `Hasta ${edadMax} años`
  return 'Sin límite de edad'
}

// La perilla que decide si la regla de la categoría obliga. Vive en el CONTENEDOR,
// no en la categoría: `grupos.tipo_restriccion` del lado de clubes y
// `torneos_categorias.tipo_restriccion` del lado de torneos. Mismo mecanismo, mismos
// valores, mismos textos -- por eso están aquí y no repetidos en cada pantalla.
//
// Los tres hacen algo REALMENTE distinto. Antes no: el código solo preguntaba
// "¿es estricta?" y mandaba los otros dos a avisar, así que "Sin restricción" era
// decorativa.
export const RESTRICTION_OPTIONS = [
  { value: 'estricta', label: 'Estricta' },
  { value: 'recomendada', label: 'Recomendada' },
  { value: 'sin_restriccion', label: 'Sin restricción' },
]

export const RESTRICTION_HINTS = {
  estricta: 'No se puede asignar a alguien fuera del rango de la categoría.',
  recomendada: 'Avisa si alguien queda fuera, pero deja continuar.',
  sin_restriccion: 'La categoría no dice nada: solo describe al grupo.',
}

// Veredictos que devuelve evaluar_categorias() en la base.
export const VEREDICTO = { PERMITIDO: 'permitido', AVISAR: 'avisar', BLOQUEAR: 'bloquear' }

export const normalizarTipoRestriccion = (valor) => {
  const v = String(valor || '').trim().toLowerCase()
  return RESTRICTION_OPTIONS.some((o) => o.value === v) ? v : 'recomendada'
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

// El eco en vivo de un límite de tackle, en español corriente.
//
// Existe para que el error se vea solo. Escribir "2016" donde va una edad producía
// una ventana en el año 10 y nada lo delataba hasta que ningún jugador cabía en su
// categoría; con el eco, "nacidos desde el 1 de enero del año 10" salta a la vista.
// Es mejor diseño que la advertencia "Años, no año de nacimiento", que le pedía al
// usuario cuidarse de una trampa que el formulario mismo ponía.
//
// `extremo`: 'max' = el jugador más grande (nace ANTES, marca el inicio de la
// ventana); 'min' = el más chico (nace DESPUÉS, marca el final).
export const ecoLimiteTackle = (edad, dia, mes, extremo, currentYear = new Date().getFullYear()) => {
  const e = num(edad)
  if (e === null) return ''
  const anio = currentYear - e
  const d = num(dia)
  const m = num(mes)
  const conCorte = d !== null && m !== null && m >= 1 && m <= 12 && d >= 1 && d <= 31
  const fecha = conCorte
    ? `${d} de ${MESES[m - 1]} de ${anio}`
    : (extremo === 'max' ? `1 de enero de ${anio}` : `31 de diciembre de ${anio}`)
  return extremo === 'max'
    ? `Admite nacidos desde el ${fecha}`
    : `Admite nacidos hasta el ${fecha}`
}
